import React, {FC, ReactNode, useCallback, useContext, useEffect, useReducer, useState} from "react";
import {BrowserRouter, Route, Routes, useLocation} from "react-router-dom";

import {ContextAppDispatch, ContextAppState} from "./context.ts";

import {useLocalStorage} from "@states/local";
import {appInitialState, appReducer} from "@states/app";
import NotFound from "@app/not-found.tsx";
import {MetaMaskButton} from "@components/metamask.tsx";
import {useTelegram} from "@hooks";
import {AccountPage, AutomatePage, AutomateRulePage} from "@app/pages";
import {AccountSendPage} from "@pages/account-send";
import {AccountReceivePage} from "@pages/account-receive";
import {backButton, init, viewport} from '@telegram-apps/sdk-react';
import {Background} from "@app/background.tsx";
import {Confetti} from "@components/ui/confetti.tsx";
import {SpeedDialPage} from "@pages/speed-dial";
import {MetaMaskUIProvider} from "@metamask/sdk-react-ui";
import {LeaderboardPage} from "@pages/leaderboard";
import {CheckInPage} from "@pages/check-in";


const WebAuthenticated: FC<{ children: ReactNode }> = ({children}) => {
    const location = useLocation();
    const {auth} = useContext(ContextAppState);
    if (auth.type === "Unauthorized") {
        return (
            <div className={"w-full flex flex-row justify-center"}>
                <MetaMaskButton redirect={location.pathname}/>
            </div>
        )
    }
    return children;
};

const TelegramAuthenticated: FC<{ children: ReactNode }> = ({children}) => {
    const location = useLocation();
    const [telegramLogin, , loading, telegramErr] = useTelegram();
    const {auth} = useContext(ContextAppState);
    const [retries, setRetries] = useState(0);
    const [manualRetry, setManualRetry] = useState(false);

    const MAX_RETRIES = 5;

    const doLogin = useCallback(() => {
        if (auth.type === "Unauthorized") {
            const abortController = new AbortController();
            telegramLogin(location.pathname, abortController);
        }
    }, [auth, location.pathname, telegramLogin]);

    useEffect(() => {
        if (retries < MAX_RETRIES && !manualRetry) {
            const timeout = setTimeout(() => {
                if (auth.type === "Unauthorized") {
                    setRetries(prev => prev + 1);
                    doLogin();
                }
            }, 1500);

            return () => clearTimeout(timeout);
        }
    }, [auth, retries, manualRetry, doLogin]);

    const handleRetry = () => {
        setRetries(0);
        setManualRetry(false);
        doLogin();
    };

    if (telegramErr) {
        return (
            <div className="text-center">
                <h1 className="text-red-700 text-xl mb-4">Telegram terminal says no</h1>
                <button
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                    onClick={handleRetry}
                >
                    Retry
                </button>
            </div>
        );
    }

    if (auth.type === "Unauthorized" && retries < MAX_RETRIES && loading) {
        return <h1 className="text-center text-blue-800 text-xl">Starting your telegram terminal</h1>;
    }

    if (auth.type === "Unauthorized" && retries >= MAX_RETRIES) {
        return (
            <div className="text-center">
                <h1 className="text-yellow-800 text-xl mb-4">We are sorry, login failed
                    after {MAX_RETRIES} attempts</h1>
                <button
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                    onClick={handleRetry}
                >
                    Retry
                </button>
            </div>
        );
    }

    return children;
};


const AppRouter = () => {
    const appState = useContext(ContextAppState);

    if (appState.type === 'Telegram') {
        return (
            <BrowserRouter
                future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true
                }}
            >
                <Routes>
                    <Route
                        path={"/account"}
                        element={
                            <TelegramAuthenticated>
                                <AccountPage/>
                            </TelegramAuthenticated>
                        }
                    />
                    <Route
                        path={"/account-send"}
                        element={
                            <TelegramAuthenticated>
                                <AccountSendPage/>
                            </TelegramAuthenticated>

                        }
                    />
                    <Route
                        path={"/account-receive"}
                        element={
                            <TelegramAuthenticated>
                                <AccountReceivePage/>
                            </TelegramAuthenticated>

                        }
                    />
                    <Route
                        path={"/leaderboard"}
                        element={
                            <TelegramAuthenticated>
                                <LeaderboardPage/>
                            </TelegramAuthenticated>
                        }
                    />
                    <Route
                        path={"/check-in"}
                        element={
                            <TelegramAuthenticated>
                                <CheckInPage/>
                            </TelegramAuthenticated>
                        }
                    />
                    <Route
                        path={"/automate/:venue"}
                        element={
                            <TelegramAuthenticated>
                                <AutomatePage/>
                            </TelegramAuthenticated>
                        }
                    />
                    <Route
                        path={"/automate/rules/:id"}
                        element={
                            <TelegramAuthenticated>
                                <AutomateRulePage/>
                            </TelegramAuthenticated>
                        }
                    />
                    <Route
                        path={"/speed-dial"}
                        element={
                            <TelegramAuthenticated>
                                <SpeedDialPage/>
                            </TelegramAuthenticated>
                        }
                    />
                    <Route path='*' element={<NotFound/>}/>
                </Routes>
                <Background/>
            </BrowserRouter>
        );
    }

    if (appState.type === 'WebApp') {
        return (
            <BrowserRouter
                future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true
                }}
            >
                <Routes>
                    <Route
                        path={"/account"}
                        element={
                            <WebAuthenticated>
                                <AccountPage/>
                            </WebAuthenticated>
                        }
                    />
                    <Route
                        path={"/account-send"}
                        element={
                            <WebAuthenticated>
                                <AccountSendPage/>
                            </WebAuthenticated>
                        }
                    />
                    <Route
                        path={"/account-receive"}
                        element={
                            <WebAuthenticated>
                                <AccountReceivePage/>
                            </WebAuthenticated>

                        }
                    />
                    <Route
                        path={"/automate/:venue"}
                        element={
                            <WebAuthenticated>
                                <AutomatePage/>
                            </WebAuthenticated>
                        }
                    />
                    <Route
                        path={"/leaderboard"}
                        element={
                            <WebAuthenticated>
                                <LeaderboardPage/>
                            </WebAuthenticated>
                        }
                    />
                    <Route
                        path={"/check-in"}
                        element={
                            <WebAuthenticated>
                                <CheckInPage/>
                            </WebAuthenticated>
                        }
                    />
                    <Route
                        path={"/automate/rules/:id"}
                        element={
                            <WebAuthenticated>
                                <AutomateRulePage/>
                            </WebAuthenticated>
                        }
                    />
                    <Route
                        path={"/speed-dial"}
                        element={
                            <WebAuthenticated>
                                <SpeedDialPage/>
                            </WebAuthenticated>
                        }
                    />
                    <Route path='*' element={<NotFound/>}/>
                </Routes>
                <Background/>
            </BrowserRouter>
        );
    }

}
const WebApp = () => {
    return (
        <MetaMaskUIProvider
            key={'netmask'}
            debug={false}
            children={<AppRouter/>}
            sdkOptions={{
                checkInstallationImmediately: false,
                logging: {
                    developerMode: false
                },
                dappMetadata: {
                    name: "Nyanbot",
                    url: "https://nyan.bot",
                }
            }}/>
    )
}

const TelegramApp = () => {
    useEffect(() => {
        init();
        if (backButton.mount.isAvailable()) {
            backButton.mount();
        }
        if (viewport.expand.isAvailable()) {
            viewport.expand();
        }
    }, []);
    return (
        <AppRouter/>
    )
}

const AppContent = () => {
    const {type} = useContext(ContextAppState);

    if (type === "WebApp") return <WebApp/>;
    return <TelegramApp/>;
};


const App = () => {
    const [localStorage, _] = useLocalStorage();
    const [appState, appDispatch] = useReducer(appReducer, appInitialState(localStorage));

    return (
        <div className={"font-sans"}>
            <ContextAppState.Provider value={appState}>
                <ContextAppDispatch.Provider value={appDispatch}>
                    {/*<Client>*/}
                    <Confetti/>
                    <AppContent/>
                    {/*</Client>*/}
                </ContextAppDispatch.Provider>
            </ContextAppState.Provider>
        </div>
    );
}

export default App


